<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { AppButton } from '@libs/ui';

import type { TripTypeOption } from './TripTypeSelectorPanel.model';
import { TripType } from './TripTypeSelectorPanel.model';
import TripTypeCard from './TripTypeCard.vue';

interface TripTypeSelectorPanelProps {
  departureAirport: string;
  departurePort: string;
  carStartPosition: string;
}
const props = defineProps<TripTypeSelectorPanelProps>();

interface TripTypeSelectorPanelEmits {
  (event: 'confirm', tripType: TripType): void;
  (event: 'select', tripType: TripType): void;
  (event: 'edit', tripType: TripType): void;
}

const emit = defineEmits<TripTypeSelectorPanelEmits>();

const selectedTripType = ref<TripType | undefined>(undefined);
const { t } = useI18n();

function onConfirm(): void {
  if (selectedTripType.value === undefined) {
    return;
  }
  emit('confirm', selectedTripType.value);
}

function setSelectedTripType(selectedType: TripType): void {
  selectedTripType.value = selectedType;
  emit('select', selectedType);
}

const tripTypes = computed<TripTypeOption[]>(() => [
  {
    tripType: TripType.Flight,
    title: t('planner.trip_type_selector_panel.flight.title'),
    description: t('planner.trip_type_selector_panel.flight.description'),
    locationLabel: t('planner.trip_type_selector_panel.flight.location_label'),
    destination: props.departureAirport,
  },
  {
    tripType: TripType.Ferry,
    title: t('planner.trip_type_selector_panel.ferry.title'),
    description: t('planner.trip_type_selector_panel.ferry.description'),
    locationLabel: t('planner.trip_type_selector_panel.ferry.location_label'),
    destination: props.departurePort,
  },
  {
    tripType: TripType.Hotel,
    title: t('planner.trip_type_selector_panel.hotel.title'),
    description: t('planner.trip_type_selector_panel.hotel.description'),
  },
  {
    tripType: TripType.Car,
    title: t('planner.trip_type_selector_panel.car.title'),
    description: t('planner.trip_type_selector_panel.car.description'),
    locationLabel: t('planner.trip_type_selector_panel.car.location_label'),
    destination: props.carStartPosition,
  },
]);
</script>

<template>
  <div class="trip-type-selector-panel">
    <header class="trip-type-selector-panel-header">
      {{ $t('planner.trip_type_selector_panel.title') }}
    </header>
    <div class="trip-type-selector-panel-body-wrapper">
      <TripTypeCard
        v-for="tripType in tripTypes.filter(
          (tripType) =>
            tripType.tripType !== TripType.Ferry || tripType.destination
        )"
        :key="tripType.tripType"
        :trip-type="tripType.tripType"
        :selected="tripType.tripType === selectedTripType"
        @click="setSelectedTripType(tripType.tripType)"
        @edit="emit('edit', tripType.tripType)"
      >
        <template #title>
          {{ tripType.title }}
        </template>
        <template #description>
          {{ tripType.description }}
        </template>
        <template #location-label>
          {{ tripType.locationLabel }}
        </template>
        <template #destination>
          {{ tripType.destination }}
        </template>
        <template #edit-text>
          {{ $t('planner.trip_type_selector_panel.edit_text') }}
        </template>
      </TripTypeCard>
    </div>
    <footer class="trip-type-selector-panel-footer">
      <AppButton
        :state="selectedTripType ? 'enabled' : 'disabled'"
        class="button"
        type="primary"
        size="large"
        @click="onConfirm"
      >
        {{ $t('planner.continue') }}
      </AppButton>
    </footer>
  </div>
</template>

<style scoped lang="scss">
.trip-type-selector-panel {
  background-color: var(--color-white);
  display: flex;
  flex-direction: column;
  z-index: var(--panel-first-level-z-index);
  height: 100%;
  overflow: auto;
  .trip-type-selector-panel-header {
    @include font-label-xxl-bold;
    color: var(--color-secondary-40);
    padding: var(--spacing-16);
  }
  .trip-type-selector-panel-body-wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-16);
    position: relative;
    overflow: auto;
    flex-grow: 1;
    padding: 0 var(--spacing-16) var(--spacing-16) var(--spacing-16);
    .save-money {
      @include font-label-l-bold;
      color: var(--color-secondary-40);
    }
  }
  .trip-type-selector-panel-footer {
    display: flex;
    justify-content: center;

    padding: var(--spacing-16);
    border-top: 1px solid var(--color-secondary-90);
    .button {
      min-width: 300px;
    }
  }
}
</style>
